<template>
  <div class="pa-5">
    <h2>
      {{ $t('serviceReport.label.serviceReport') }}
    </h2>

    <v-data-table
      :no-data-text="$t('table.noResults')"
      :loading="isLoading"
      :items="serviceReport.items"
      :items-per-page="1000"
      :headers="headers"
      mobile-breakpoint="20"
      item-key="id"
      hide-default-footer
      disable-sort
      single-expand
      show-expand
    >
      <template v-slot:item.item.name="{ item }">
        <span class="d-block text-truncate">
          {{ item.item.name ? item.item.name : '---' }}
        </span>
      </template>
      <template v-slot:item.item.quantity="{ item }">
        <span class="d-block">
          {{ item.quantity ? item.quantity : '---' }}
        </span>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="ma-0 pa-0">
          <v-card class="pa-5 grey lighten-4" elevation="0" tile flat>
            <v-row>
              <v-col cols="4">
                <p class="h6 mb-0 grey--text caption">
                  {{ $t('serviceReport.label.category') }}
                </p>
                <p class="body-2">
                  {{
                    item.item.category.name
                      ? item.item.category.name
                      : '---'
                  }}
                </p>
              </v-col>
              <v-col cols="8">
                <p class="h6 mb-0 grey--text caption">
                  {{ $t('serviceReport.label.location') }}
                </p>
                <p class="body-2">
                  {{ item.location ? item.location : '---' }}
                </p>
              </v-col>
            </v-row>

            <p class="h6 mb-0 grey--text caption">
              {{ $t('serviceReport.label.itemDescription') }}
            </p>
            <p class="body-2">
              {{ item.item.description ? item.item.description : '---' }}
            </p>

            <p class="h6 mb-0 grey--text caption">
              {{ $t('serviceReport.label.serviceDescription') }}
            </p>
            <p class="body-2 mb-0">
              {{ item.description ? item.description : '---' }}
            </p>
          </v-card>
        </td>
      </template>
    </v-data-table>

    <RentOutMessages
      :appointment="appointment"
      :appointmentTerms="appointmentTerms"
      :appointmentTermsSigned="appointmentTermsSigned"
      :serviceReport="serviceReport"
    />

    <div v-if="quoteItems">
      <v-divider class="mt-5 mb-10" />

      <div class="d-flex align-center my-8">
        <h2>
          {{ $t('serviceReport.label.quote') }}
        </h2>
        <v-btn
          v-if="quoteItems && isRentOut"
          @click="redirectToPrintPage()"
          class="ml-5"
          color="primary"
          outlined
        >
          <v-icon left> mdi-printer-eye </v-icon>
          <span class="body-2">
            {{ $t('quote.label.printableVersion') }}
          </span>
        </v-btn>
      </div>

      <AppointmentQuoteInfo
        :vendor="vendor"
        :isLoading="isLoading"
        :appointment="appointment"
        :isQuotePending="isQuotePending"
        :isQuoteAlreadyPaid="isQuoteAlreadyPaid"
        :quoteWasNotFound="quoteWasNotFound"
      />

      <QuoteItemsList
        :isQuoteAlreadyPaid="isQuoteAlreadyPaid"
        :isQuotePending="isQuotePending"
        :quoteItems="quoteList"
        :quote="quote"
        :appointment="appointment"
        :version="version"
      />

      <QuotePrices
        v-if="version != 2"
        :quotes="quote"
        :quoteItems="quote"
        :appointmentType="appointment.type"
        :isQuoteAlreadyPaid="isQuoteAlreadyPaid"
      />

      <QuotePricesV2
        v-else
        :quotes="quote"
        :quoteItems="quote"
        :appointmentType="appointment.type"
        :isQuoteAlreadyPaid="isQuoteAlreadyPaid"
      />

      <QuoteWaitingPaymentMessage
        :breakPointChange="breakPointChange"
        :hasNoPayment="hasNoPayment"
        :isRentOut="isRentOut"
      />
    </div>
  </div>
</template>

<script>
import QuoteItemsList from '@/views/quote/components/QuoteItemsList'
import QuotePrices from '@/views/quote/components/QuotePrices'
import QuotePricesV2 from '@/views/quote/components/QuotePricesV2'
import RentOutMessages from './rentOut/RentOutMessages'
import AppointmentQuoteInfo from './quote/AppointmentQuoteInfo'
import AppointmentFilesTermList from './terms/AppointmentFilesTermList'
import QuoteWaitingPaymentMessage from '@/views/quote/components/QuoteWaitingPaymentMessage'

import { mapGetters } from 'vuex'
import { headers } from '@/views/listHeaders/serviceReportHeaders'
import { rentOutTypes } from '@/components/appointment/formFieldsCreate/appointmentFormDefaults'

export default {
  name: 'AppointmentReportAndQuotes',

  components: {
    QuotePrices,
    QuotePricesV2,
    QuoteItemsList,
    RentOutMessages,
    AppointmentQuoteInfo,
    AppointmentFilesTermList,
    QuoteWaitingPaymentMessage,
  },

  data() {
    return {
      headers: headers,
      isQuoteAlreadyPaid: false,
      isQuotePending: false,
      vendor: null,
    }
  },

  computed: {
    ...mapGetters([
      'isLoading',
      'appointment',
      'serviceReport',
      'quoteWasNotFound',
      'appointmentTerms',
      'appointmentTermsSigned',
    ]),

    breakPointChange() {
      return !this.$vuetify.breakpoint.mobile && 'mw-400'
    },

    quoteItems() {
      return this.quote?.items
    },

    quote() {
      return this.serviceReport?.quotes[0]
    },

    isRentOut() {
      return rentOutTypes.includes(this.appointment.type)
    },

    version() {
      return this.quote?.version ?? 1
    },

    hasNoPayment() {
      return this.quote?.charge === null
    },

    hasVendorFee() {
      return this.quote?.vendor_service_charge_fee > 0
    },

    vendorFee() {
      return this.quote?.vendor_service_charge_fee && this.quote?.vendor_service_charge_fee
    },

    vendorFeeData() {
      if (!this.hasVendorFee) return
      return {
        id: '1',
        date_created: '',
        item_id: '1',
        item_name: this.$t('quote.priceTable.serviceCallFee'),
        item_description: this.$t('quote.priceTable.technicalVisitDescription'),
        item_category_id: '',
        item_category_name: '',
        service_report_item_id: '',
        service_report_item_location: '',
        service_report_item_description: '',
        service_report_item_quantity: 1,
        calculated_price: this.vendorFee,
        calculated_by_installment_price: 0,
        accepted: true,
      }
    },

    quoteList() {
      const list = this.quoteItems
      if (this.version == 2 && this.vendorFeeData) list.push(this.vendorFeeData)
      return list
    },
  },

  methods: {
    redirectToPrintPage() {
      window.open(`/quote/${this.serviceReport.id}?redirect=false`, '_blank')
    },

    checkChargeStatus(status) {
      status === 'RECEIVED' || status === 'CONFIRMED'
        ? (this.isQuoteAlreadyPaid = true)
        : (this.isQuoteAlreadyPaid = false)

      status === 'PENDING'
        ? (this.isQuotePending = true)
        : (this.isQuotePending = false)
    },
  },

  mounted() {
    Promise.resolve(this.serviceReport)
      .then(async (report) => await report?.quotes[0])
      .then((quote) => {
        const status = quote?.charge?.installments[0]?.status
        const vendor = quote?.price_table?.vendor

        if (vendor) this.vendor = vendor
        if (status) this.checkChargeStatus(status)
      })
  }
}
</script>
